import ChartClient from '../client/chart/ChartClient';
import { query } from '@trlogic/client-js';
import { ChartType, Syntax } from '@trlogic/ui-pack';
import MessageUtil from '../util/MessageUtil';
import { Chart, ChartEditorOptions, ChartExecuteType, ChartJsDataModel, ChartJsonModel, MetricItem } from '../domain/chart/ChartDomains';
import ChartUtil from '../util/ChartUtil';
import Query = query.Query;
import { CaseChartPanel } from '../domain/case/CaseDomains';
import { ChartSchema } from '../../../server/src/lib/domain/chart/ChartDomain';
import LanguageUtil from '../util/LanguageUtil';

class ChartService {
  private chartClient: ChartClient;

  constructor(chartClient: ChartClient) {
    this.chartClient = chartClient;
  }

  public async simulateChartForCase(chartId: string, panel: CaseChartPanel, transactionValue: string) {
    return await this.chartClient.simulateCharForCase(chartId, panel, transactionValue);
  }

  public async getChartById(id: string): Promise<ChartJsonModel> {
    return await this.chartClient.getChartById(id);
  }

  public async getChartSchemaById(id: string): Promise<ChartSchema> {
    return await this.chartClient.getChartSchemaById(id);
  }

  public async save(id: string, chart: Chart, chartOptions: ChartEditorOptions, isUpdate?: boolean): Promise<string> {
    try {
      if (!this.isChartValid(chart)) return Promise.reject();
      if (!this.chartEditorOptionsValid(chartOptions, chart.type as ChartType)) return Promise.reject();

      if (!isUpdate) {
        const isExist = await this.exist(id!);
        if (isExist) {
          MessageUtil.error({
            summary: LanguageUtil.get({ id: 'messages.error.savingChart' }),
            detail: `Chart with name ${id} already exist`,
          });
          return Promise.reject();
        }
      }

      const dto = this.prepareChart(chart, chartOptions);
      const response: string = await this.chartClient.save(dto!);

      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'messages.success.chartSaved', defaultMessage: 'Chart saved successfully!' }),
        detail: LanguageUtil.get({ id: 'messages.success.saved', defaultMessage: `${chart.name} saved successfully` }, { 0: `${chart.name}` }),
      });
      return response;
    } catch (e) {
      console.log(e);
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.savingChart' }),
        detail: LanguageUtil.get({ id: 'messages.error.cannotSave', defaultMessage: `Error while saving ${chart.name}` }, { 0: `${chart.name}` }),
      });
      return Promise.reject();
    }
  }

  public async remove(id: string): Promise<any> {
    try {
      await this.chartClient.remove(id);
      MessageUtil.deleteMessage(id);
    } catch (e) {
      console.log(e);
    }
  }

  public async simulate(chart: Chart, chartOptions: ChartEditorOptions): Promise<any> {
    try {
      const dto = this.prepareChart(chart, chartOptions);
      if (dto == null) return;
      const response: ChartJsDataModel = await this.chartClient.simulate(dto);
      return ChartUtil.setColorPalette(chartOptions, response, chart.type as ChartType);
    } catch (e) {
      console.log(e);
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.simulatingChart', defaultMessage: 'Error while simulating chart' }),
      });
    }
  }

  public async execute(chartId: string, chartOptions?: ChartEditorOptions, chartType?: ChartType): Promise<any> {
    const response: ChartJsDataModel = await this.chartClient.execute(chartId);
    if (response == null) return response;
    if (chartOptions == null || chartType == null) return response;
    return ChartUtil.setColorPalette(chartOptions, response, chartType);
  }

  public async exist(id: string): Promise<boolean> {
    return await this.chartClient.exist(id);
  }

  public async count(query: Query): Promise<number> {
    return await this.chartClient.count(query);
  }

  public parseExpression(syntax: Syntax) {
    const iterateSyntax = (syntax: Syntax): Syntax => {
      return {
        id: syntax.id,
        value: syntax.value,
        children: syntax.children?.map((c) => iterateSyntax(c)),
      };
    };
    return iterateSyntax(syntax);
  }

  private isChartValid = (chart: Chart): boolean => {
    if (chart.name == null || chart.name.trim() === '') {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.savingChart' }),
        detail: LanguageUtil.get({ id: 'messages.error.chartName', defaultMessage: 'Chart name is required' }),
      });
      return false;
    }
    if (chart.type == null || chart.type.trim() === '') {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.savingChart' }),
        detail: LanguageUtil.get({ id: 'messages.error.chartType', defaultMessage: 'Chart type is required' }),
      });
      return false;
    }
    if (chart.dashboardId == null || chart.dashboardId.trim() === '') {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.savingChart' }),
        detail: LanguageUtil.get({ id: 'messages.error.dasboardReq', defaultMessage: 'Dashboard is required' }),
      });
      return false;
    }
    return true;
  };

  private prepareChart(chart: Chart, chartOptions: ChartEditorOptions): ChartJsonModel | null {
    const metricItem = chartOptions.metricItem.map((item: MetricItem) => {
      if (item.metric.filter) item.metric.filter = this.parseExpression(item.metric.filter);
      if (item.metric.transformer) item.metric.transformer = this.parseExpression(item.metric.transformer);
      return item;
    });

    if (chartOptions.segmentItem != null && chartOptions.segmentItem.segments.filter != null) {
      chartOptions.segmentItem.segments.filter!.items = chartOptions.segmentItem.segments.filter!.items.map((item) => {
        item.syntax = this.parseExpression(item.syntax);
        return item;
      });
    }

    if (chartOptions.globalFilterItem != null) {
      chartOptions.globalFilterItem.syntax = this.parseExpression(chartOptions.globalFilterItem.syntax);
    }
    const jsonModel = {
      id: chart.id || 'TEST_ID',
      name: chart.name || 'TEST CHART',
      description: chart.description || '',
      dataSource: chart.dataSource!,
      type: chart.type!,
      dashboardId: chart.dashboardId as string,
      createdDate: chart.createdDate || new Date().valueOf(),
      updatedDate: chart.updatedDate || new Date().valueOf(),
      executionType: this.getChartExecuteType(chartOptions),
      visualOptions: chart.visualOptions,
      chartEditorOptions: {
        segmentItem: chartOptions.segmentItem,
        metricItem: metricItem,
        globalFilterItem: chartOptions.globalFilterItem!,
        breakdown: chartOptions.breakdown || null,
        endDate: chartOptions.endDate,
        startDate: chartOptions.startDate,
      },
    };
    return jsonModel;
  }

  private chartEditorOptionsValid(chartOptions: ChartEditorOptions, chartType: ChartType): boolean {
    if (chartType == ChartType.INDICATOR && chartOptions.segmentItem) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.savingChart' }),
        detail: LanguageUtil.get({ id: 'messages.error.cannotUseSegment', defaultMessage: 'You can not use segment item for this chart type' }),
      });
      return false;
    }
    if (chartType == ChartType.INDICATOR && chartOptions.metricItem?.length <= 0) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.savingChart' }),
        detail: LanguageUtil.get({ id: 'messages.error.leastOneMetric', defaultMessage: 'You must select at least one metric for indicator chart' }),
      });
      return false;
    }
    if (chartType != ChartType.INDICATOR && (chartOptions.metricItem?.length <= 0 || !chartOptions.segmentItem)) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.savingChart' }),
        detail: LanguageUtil.get({ id: 'messages.error.leastOneMetricSegment', defaultMessage: 'You must select at least one metric and segment item' }),
      });
      return false;
    }
    return true;
  }

  public getChartExecuteType(chartEditorOptions: ChartEditorOptions): ChartExecuteType {
    const segmentItem = chartEditorOptions.segmentItem;
    const metrics = chartEditorOptions.metricItem;
    const breakdown = chartEditorOptions.breakdown;
    if (!breakdown && segmentItem && metrics?.length == 1) {
      return ChartExecuteType.SINGLE;
    } else if (!breakdown && segmentItem && metrics && metrics?.length >= 1) {
      return ChartExecuteType.MULTI;
    } else if (breakdown && segmentItem && metrics && metrics?.length >= 1) {
      return ChartExecuteType.BREAKDOWN;
    } else if (!breakdown && !segmentItem && metrics) {
      return ChartExecuteType.INDICATOR;
    } else {
      throw new Error('Unsupported chart response converter type');
    }
  }
}

export default ChartService;
